// ErrorScreen — generic full-screen error / empty-state for Phase 4 entry flow.
//
// Designed to be reused for "ルームが見つかりません", "ネットワークエラー",
// "サーバが利用不可" 等. Phase 6 でルーム作成 UX をリワークする際に
// もっとリッチな画面に差し替える前提で、構造は最小限のみ.

export interface ErrorScreenProps {
  /** Heading line. e.g. "ルームが見つかりません". */
  readonly title: string;
  /** Body text. 1〜2 sentence. */
  readonly description: string;
  /** Optional CTA. クリックすると onActivate を呼ぶ. */
  readonly cta?: {
    readonly label: string;
    readonly onActivate: () => void;
  };
  /** Subtle technical details (status code, error code) — collapsed below. */
  readonly detail?: string;
}

export const ErrorScreen = ({
  title,
  description,
  cta,
  detail,
}: ErrorScreenProps): React.ReactNode => {
  return (
    // p-4 + w-full max-w: 固定 w-[420px] はスマホ縦 (390/360px) で viewport を
    // 超えていた (2026-07-03 実機 fb)。ReJoinScreen カードと同じ「cap + 全幅追従」
    // パターンで小画面に収める (デスクトップは max-w が同値なので見た目不変)。
    <div className="grid h-full place-items-center p-4" role="alert">
      <div className="w-full max-w-[420px] rounded border border-white/10 bg-[var(--color-panel)] p-6 shadow-xl">
        <h1 className="mb-3 text-center text-base font-bold text-[var(--color-error)]">{title}</h1>
        <p className="mb-4 text-center text-xs text-[var(--color-text)]/85">{description}</p>
        {cta && (
          <button
            type="button"
            onClick={cta.onActivate}
            className="w-full rounded bg-[var(--color-accent)] px-4 py-2 text-sm font-bold text-[var(--color-panel)] transition hover:brightness-110"
          >
            {cta.label}
          </button>
        )}
        {detail !== undefined && detail.length > 0 && (
          <p className="mt-3 break-all text-center font-mono text-[10px] text-[var(--color-text-muted)]">
            {detail}
          </p>
        )}
      </div>
    </div>
  );
};
